import Button from "@components/common/Button.jsx";
import FilteringIcon from "@components/icons/FilteringIcon.jsx";
import { useClickOutside } from "@hooks/useClickOutside.js";
import { Tooltip } from "flowbite-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * FilterDropdown component
 *
 * @desc Renders a toggle button that shows/hides a dropdown panel containing a list of filter checkboxes.
 * Clicking outside the dropdown closes it.
 * @param {Array<{id: string, name: string, enabled: boolean, visible: boolean}>} filters - Array of filter objects
 * @param {Function} toggleFilter - Callback to toggle a filter's enabled state by ID
 * @param {React.Element} [buttonIcon] - Optional icon for the toggle button
 * @returns {JSX.Element}
 */
const FilterDropdown = ({ filters, toggleFilter, buttonIcon = FilteringIcon }) => {
	const containerRef = useRef(null);
	const [isVisible, setIsVisible] = useState(false);
	const { t } = useTranslation();

	const toggleIsListVisible = () => setIsVisible((prev) => !prev);

	// Hide dropdown when clicking outside
	useClickOutside(containerRef, () => setIsVisible(false));

	return (
		<div className={"relative"} ref={containerRef}>
			<Tooltip
				arrow={false}
				content={t("common:filterDropdown.tooltip")}
				className={"filter-dropdown w-max border border-custom-border bg-dark-blue font-normal"}
			>
				<Button className={"mt-[2.1em]"} icon={buttonIcon} onClick={toggleIsListVisible} />
			</Tooltip>
			{isVisible && filters?.length > 0 && (
				<div className="absolute left-0 z-30 mt-1 w-max min-w-52 max-w-58 rounded-md border border-custom-border bg-dark-blue px-4 pb-4 shadow-lg">
					<h3 className="mt-3 mb-3 text-start font-bold text-sm text-white tracking-wide">
						{t("common:filterDropdown.title")}
					</h3>
					<ul className="space-y-0.5">
						{filters
							.filter((filter) => filter.enabled)
							.map((filter) => (
								<li
									key={filter.id}
									className="flex w-fit items-center gap-2 rounded-md px-2 py-2 transition-colors hover:bg-dark-blue/40"
								>
									<input
										type="checkbox"
										checked={filter.visible}
										name={filter.name}
										id={filter.id}
										onChange={() => toggleFilter(filter.id)}
										className="!bg-blue-gray h-4 w-4 rounded border-blue-gray ring-offset-gray-800 focus:ring-3 focus:ring-blue-gray"
									/>
									<label
										htmlFor={filter.id}
										className="flex-1 cursor-pointer select-none text-start text-white"
									>
										{t(filter.name)}
									</label>
								</li>
							))}
					</ul>
				</div>
			)}
		</div>
	);
};

export default FilterDropdown;
